<template>
  <v-card>
    <v-card-title class="primary white--text">
      <span class="headline">{{ $t('allocations') }} - {{ tractContent.species }}</span>
      <v-spacer/>
      <Icon
        :small="false"
        :large="true"
        icon="mdi-close"
        icon-color="tertiary"
        dataTestId="allocations-close"
        :tooltipText="$t('close')"
        @icon-clicked="$emit('close')"
      />
    </v-card-title>
    <v-card-text class="mt-4">
      <v-row dense>
        <v-col cols="12">
          <v-chip small class="mb-2 mr-2">
            <span class="font-weight-bold">{{ $t('expectedMBF') }}: {{ numberWithCommas(expectedMBF, 3) }}</span>
          </v-chip>
          <v-chip small class="mb-2 mr-2" :color="unallocated < 0 ? 'error' : 'success'" dark>
            <span class="font-weight-bold">{{ $t('unallocated') }}: {{ numberWithCommas(unallocated, 3) }}</span>
          </v-chip>
        </v-col>
      </v-row>
      <v-data-table
        :items="allocations"
        :headers="headers"
        :hide-default-footer="allocations.length < 10"
        dense
        :no-data-text="$t('noAllocations')"
        :loading="loading"
      >
        <template #item.account="{ item }">
          <span>{{ item.destinationAccountName }}</span>
        </template>
        <template #item.expected="{ item }">
             <span>{{ numberWithCommas(item.expected / NumericConstants.MbfDivisor, 3) }}</span>
        </template>
        <template #item.actions="{ item }">
          <Icon
            icon="mdi-pencil"
            iconColor="success"
            elementId="allocation-edit-button"
            :tooltipText="$t('edit')"
            @icon-clicked="beginEditAllocation(item)"
          />
          <Icon
            icon="mdi-delete-forever"
            iconColor="error"
            elementId="allocation-delete-button"
            :tooltipText="$t('delete')"
            @icon-clicked="confirmDeleteAllocation(item)"
          />
        </template>
      </v-data-table>

      <v-divider class="my-4"/>

      <div v-if="editingAllocation">
        <span class="subtitle-1 font-weight-bold">{{ $t('editAllocation', { tractDestination: editingAllocation.destinationAccountName }) }}</span>
        <FormWrapper
          ref="editForm"
          formRef="editAllocationForm"
          :lazyValidation="true"
          :buttonText="$t('save')"
          @submit="saveEditedAllocation"
        >
          <v-container fluid>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  :value="editingAllocation.destinationAccountName"
                  :label="$t('destination')"
                  disabled
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editExpected"
                  :label="$t('expectedMBF')"
                  :rules="[rules.required, rules.positiveDecimal, maxEditAllocationRule]"
                  color="black"
                  type="number"
                  hide-spin-buttons
                />
              </v-col>
            </v-row>
          </v-container>
        </FormWrapper>
        <v-btn text @click="cancelEdit">{{ $t('cancel') }}</v-btn>
      </div>

      <div v-else-if="unallocated > 0 && availableDestinations.length > 0">
        <span class="subtitle-1 font-weight-bold">{{ $t('newAllocation') }}</span>
        <FormWrapper
          ref="form"
          formRef="allocationForm"
          :lazyValidation="true"
          :buttonText="$t('create')"
          :disabled="availableDestinations.length === 0"
          @submit="createNewAllocation"
        >
          <v-container fluid>
            <v-row>
              <v-col cols="12" md="6">
                <v-select
                  v-model="selectedDestination"
                  :items="availableDestinations"
                  :label="$t('destination')"
                  :rules="[rules.required]"
                  item-text="destinationAccountName"
                  return-object
                  :no-data-text="$t('noDestinationsAvailable')"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="newExpected"
                  :label="$t('expectedMBF')"
                  :rules="[rules.required, rules.positiveDecimal, maxAllocationRule]"
                  color="black"
                  type="number"
                  hide-spin-buttons
                />
              </v-col>
            </v-row>
          </v-container>
        </FormWrapper>
      </div>
    </v-card-text>

    <v-dialog v-model="deleting" width="400px">
      <ConfirmDelete
        v-if="deleting"
        :title="$t('allocation')"
        @delete="deleteSelectedAllocation"
        @cancel-delete="deleting = false"
      />
    </v-dialog>
  </v-card>
</template>

<script>
import { mapActions } from 'vuex'
import FieldRules from '@/utils/rules.js'
import { numberWithCommas } from '@/utils/NumericMutations'
import { NumericConstants } from '@/utils/constants'

export default {
  name: 'TractContentAllocations',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    FormWrapper: () => import('@/components/core/FormWrapper.vue'),
    ConfirmDelete: () => import('@/components/helper/ConfirmDelete.vue')
  },

  props: {
    tractContent: {
      type: Object,
      required: true
    },
    tractId: {
      type: Number,
      required: true
    }
  },

  data: () => ({
    allocations: [],
    tractDestinations: [],
    loading: true,
    selectedDestination: null,
    newExpected: null,
    editingAllocation: null,
    editExpected: null,
    deleting: false,
    focusedAllocation: null,
    rules: FieldRules.rules,
    NumericConstants,
    filteredDestinations: []
  }),

  computed: {
    headers () {
      return [
        { text: this.$t('destination'), value: 'destinationAccountName', align: 'left' },
        { text: this.$t('expectedMBF'), value: 'expected', align: 'right' },
        { text: this.$t('actions'), value: 'actions', align: 'right', sortable: false }
      ]
    },

    expectedMBF () {
      return this.tractContent.expected / NumericConstants.MbfDivisor
    },

    allocatedTotal () {
      return this.allocations.reduce((sum, a) => sum + a.expected, 0)
    },

    unallocated () {
      return this.expectedMBF - (this.allocatedTotal / NumericConstants.MbfDivisor)
    },

    availableDestinations () {
      return this.filteredDestinations
    },

    maxAllocationRule () {
      return (v) => {
        if (v === null || v === undefined || v === '') return true
        return parseFloat(v) <= this.unallocated || this.$t('cannotExceedNMbf', { n: this.unallocated })
      }
    },

    maxEditAllocationRule () {
      return (v) => {
        if (v === null || v === undefined || v === '') return true
        const available = this.unallocated + this.editingAllocation.expected / NumericConstants.MbfDivisor
        return parseFloat(v) <= available || this.$t('cannotExceedNMbf', { n: available })
      }
    }
  },

  async created () {
    if (this.tractContent.tractContentAllocations) {
      this.allocations = this.tractContent.tractContentAllocations
      this.loading = true
      this.tractDestinations = await this.fetchTractDestinations(this.tractId)
      this.loading = false
      await this.filterAvailableDestinations()
    } else {
      await this.refresh()
    }
  },

  methods: {
    ...mapActions('tract-contents', ['fetchTractContentAllocations', 'createTractContentAllocation', 'updateTractContentAllocation', 'deleteTractContentAllocation']),
    ...mapActions('tract-destination', ['fetchTractDestinations']),
    ...mapActions('product', ['fetchProductById']),
    numberWithCommas,

    async refresh () {
      this.loading = true
      try {
        const [allocations, destinations] = await Promise.all([
          this.fetchTractContentAllocations({
            tractId: this.tractId,
            tractContentId: this.tractContent.tractContentId
          }),
          this.fetchTractDestinations(this.tractId)
        ])
        // Normalize allocations so destinationAccountName is always present
        this.allocations = allocations.map(a => ({
          ...a,
          destinationAccountName: a.destinationAccountName || a.accountName || ''
        }))
        this.tractDestinations = destinations
        await this.filterAvailableDestinations()
      } finally {
        this.loading = false
      }
    },

    async createNewAllocation () {
      if (!this.$refs.form.$refs.allocationForm.validate()) return

      await this.createTractContentAllocation({
        tractId: this.tractId,
        tractContentId: this.tractContent.tractContentId,
        destinationAccountId: this.selectedDestination.destinationAccountId,
        expected: Math.round(this.newExpected * NumericConstants.MbfDivisor)
      })

      this.selectedDestination = null
      this.newExpected = null
      this.$refs.form.$refs.allocationForm.resetValidation()
      await this.refresh()
      this.$emit('allocations-changed')
    },

    beginEditAllocation (allocation) {
      this.editingAllocation = allocation
      this.editExpected = allocation.expected / NumericConstants.MbfDivisor
    },

    cancelEdit () {
      this.editingAllocation = null
      this.editExpected = null
    },

    async saveEditedAllocation () {
      if (!this.$refs.editForm.$refs.editAllocationForm.validate()) return

      await this.updateTractContentAllocation({
        tractId: this.tractId,
        tractContentId: this.tractContent.tractContentId,
        tractContentAllocationId: this.editingAllocation.tractContentAllocationId,
        destinationAccountId: this.editingAllocation.destinationAccountId,
        expected: Math.round(this.editExpected * NumericConstants.MbfDivisor)
      })

      this.editingAllocation = null
      this.editExpected = null
      await this.refresh()
      this.$emit('allocations-changed')
    },

    confirmDeleteAllocation (allocation) {
      this.focusedAllocation = allocation
      this.deleting = true
    },

    async deleteSelectedAllocation () {
      await this.deleteTractContentAllocation({
        tractId: this.tractId,
        tractContentId: this.tractContent.tractContentId,
        tractContentAllocationId: this.focusedAllocation.tractContentAllocationId
      })

      this.deleting = false
      this.editingAllocation = false
      this.focusedAllocation = null
      this.$emit('allocations-changed')
      await this.refresh()
    },

    async filterAvailableDestinations () {
      const allocatedAccountIds = new Set(this.allocations.map(a => a.destinationAccountId))

      const uniqueProductIds = [...new Set(
        this.tractDestinations.flatMap(td => td.products.map(p => p.productId).filter(Boolean))
      )]

      const products = await Promise.all(uniqueProductIds.map(id => this.fetchProductById({ productId: id })))
      const productMap = Object.fromEntries(
        uniqueProductIds.map((id, i) => [id, products[i]]).filter(([, p]) => p)
      )

      this.filteredDestinations = this.tractDestinations.filter(td => {
        if (allocatedAccountIds.has(td.destinationAccountId) || td.isExternal) return false
        return td.products.some(prod => {
          const product = productMap[prod.productId]
          return product?.productSpecies?.some(s => s.speciesId === this.tractContent.speciesId)
        })
      })
    }
  }
}
</script>
